import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import classNames from "classnames";
import { contrastColor } from "utils/colors";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

interface BaseCellProps {
  className?: string;
  blocked?: boolean;
  changed?: boolean;
  children?: React.ReactNode;
  color: string;
  dimmed?: boolean;
  interactive?: boolean;
  isHighlight?: boolean;
  onClick?: () => void;
  selected?: boolean;
  style?: Record<string, any>;
  size?: "small" | "medium";
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  ariaLabel?: string;
  modeCell?: "comparator";
}

const ZOOM = { small: 0.5, medium: 1 };

const borderCss = (color: string, selected?: boolean, changedCell?: boolean) => {
  if (changedCell) {
    return "4px dotted orange";
  }

  return selected ? `1px dotted ${contrastColor(color)}` : "none";
}

const BaseCell = ({
  blocked,
  changed,
  children,
  color,
  dimmed,
  interactive,
  isHighlight,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onKeyDown,
  ariaLabel,
  selected,
  style,
  size = "medium",
  modeCell
}: BaseCellProps) => {
  const baseStyle: React.CSSProperties = {
    position: "relative",
    height: `50px`,
    width: `50px`,
    boxSizing: "border-box",
    background: color,
    color: contrastColor(color),
    border: borderCss(color, selected, modeCell === "comparator" && changed),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
    alignItems: "center",
    userSelect: "none",
    opacity: blocked || dimmed ? (selected ? 1 : 0.6) : 1,
    flexShrink: 0,
    zoom: ZOOM[size],
    ...(style as React.CSSProperties),
  };

  const content = (
    <>
      {isHighlight && (
        <>
          <div style={{ height: `7%`, position: "absolute", background: "red", top: "0", width: "100%" }} />
          <span aria-hidden="true" style={{ position: "absolute", top: 0, fontSize: "8px", color: "red", fontWeight: "bold", lineHeight: 1 }}>!</span>
        </>
      )}
      {children}
      {blocked && <BlockIcon color="error" />}
      {changed && modeCell !== "comparator" && <EditIcon sx={{ position: "absolute", right: 0, bottom: 0, fontSize: "16px" }} />}
      {changed && modeCell === "comparator" && <CompareArrowsIcon sx={{ position: "absolute", right: 0, bottom: 0, fontSize: "16px" }} />}
    </>
  );

  if (interactive && onClick && !blocked) {
    return (
      <button
        type="button"
        className={classNames("gridCell", { interactive, blocked })}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
          onKeyDown?.(e);
        }}
        aria-label={ariaLabel}
        style={{ ...baseStyle, padding: 0, appearance: "none", cursor: "pointer" }}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={classNames("gridCell", { interactive, blocked })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      style={baseStyle}
    >
      {content}
    </div>
  );
};

export default BaseCell;
